import type * as lazyIndex from './lazyIndex';
import * as SxSEventNames from './utils/eventNames';
import { LazyAction, LazyModule } from 'owa-bundling';

export const lazyModule: LazyModule<typeof lazyIndex> = new LazyModule<typeof lazyIndex>(
    () => import(/* webpackChunkName: "SxSStore" */ './lazyIndex'),
    { name: 'SxSStore' }
);

export const lazyCloseComposeInSxS: LazyAction<
    typeof lazyIndex.closeComposeInSxS,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.closeComposeInSxS);
export const lazyCreateImageGalleryFromAttachmentViewStateIds: LazyAction<
    typeof lazyIndex.createImageGalleryFromAttachmentViewStateIds,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.createImageGalleryFromAttachmentViewStateIds);
export const lazyForceCloseSxS: LazyAction<typeof lazyIndex.forceCloseSxS, typeof lazyModule> =
    new LazyAction(lazyModule, m => m.forceCloseSxS);
export const lazyLogUsageWithSxSCustomData: LazyAction<
    typeof lazyIndex.logUsageWithSxSCustomData,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.logUsageWithSxSCustomData);
export const lazyRemoveSxSViewFromDOM: LazyAction<
    typeof lazyIndex.removeSxSViewFromDOM,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.removeSxSViewFromDOM);
export const lazyOnBeforeImageGalleryItemSelected: LazyAction<
    typeof lazyIndex.onBeforeImageGalleryItemSelected,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.onBeforeImageGalleryItemSelected);
export const lazyOnChangeByImageGallery: LazyAction<
    typeof lazyIndex.onChangeByImageGallery,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.onChangeByImageGallery);
export const lazyOnPendingNavigation: LazyAction<
    typeof lazyIndex.onPendingNavigation,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.onPendingNavigation);
export const lazyOnPreviewOperationFinished: LazyAction<
    typeof lazyIndex.onPreviewOperationFinished,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.onPreviewOperationFinished);
export const lazyOnPreviewOperationStart: LazyAction<
    typeof lazyIndex.onPreviewOperationStart,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.onPreviewOperationStart);
export const lazyOpenComposeInSxS: LazyAction<
    typeof lazyIndex.openComposeInSxS,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.openComposeInSxS);
export const lazyReloadTextPreview: LazyAction<
    typeof lazyIndex.reloadTextPreview,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.reloadTextPreview);
export const lazyShowSxS: LazyAction<typeof lazyIndex.showSxS, typeof lazyModule> = new LazyAction(
    lazyModule,
    m => m.showSxS
);
export const lazyTryCloseSxS: LazyAction<typeof lazyIndex.tryCloseSxS, typeof lazyModule> =
    new LazyAction(lazyModule, m => m.tryCloseSxS);
export const lazySaveToCloudFromSxS: LazyAction<
    typeof lazyIndex.saveToCloudFromSxS,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.saveToCloudFromSxS);
export const lazySaveToCloud: LazyAction<typeof lazyIndex.saveToCloud, typeof lazyModule> =
    new LazyAction(lazyModule, m => m.saveToCloud);
export const lazyOpenSxSInANewWindow: LazyAction<
    typeof lazyIndex.openSxSInANewWindow,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.openSxSInANewWindow);
export const lazyOnShowCustomPreview: LazyAction<
    typeof lazyIndex.onShowCustomPreview,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.onShowCustomPreview);
export const lazyTryGetAttachmentFullViewStateByIdInReadingPane: LazyAction<
    typeof lazyIndex.tryGetAttachmentFullViewStateByIdInReadingPane,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.tryGetAttachmentFullViewStateByIdInReadingPane);
export const lazyWaitForPreviewLoadOrThrowError: LazyAction<
    typeof lazyIndex.waitForPreviewLoadOrThrowError,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.waitForPreviewLoadOrThrowError);

export {
    showSxS,
    onSxSReadingPaneChange,
    onBeforeSxSReadingPaneChange,
    onComposeStateChange,
    onCustomizationChange,
} from './actions/publicActions';

export { default as moveSxSStoreMutator } from './mutators/moveSxSStoreMutator';

export { default as getOrCreateSxSStoreData } from './store/Store';
export { SxSEventNames };
export { default as getActiveSxSId } from './utils/getActiveSxSId';
export {
    default as isSxSDisplayed,
    isSxSDisplayedForAttachment,
    isSxSDisplayedWithCompose,
} from './utils/isSxSDisplayed';
export { default as isAnySxSDisplayedInMainWindow } from './utils/isAnySxSDisplayed';
export { SxSCustomization, SXSID_MAIN_WINDOW, SXSV2_CONTAINER_ID } from './store/schema/SxSStore';
export type { SxSStoreData } from './store/schema/SxSStore';
export { SxSReadingPaneMode } from './store/schema/SxSReadingPaneState';
export type { SxSConversationReadingPaneState } from './store/schema/SxSReadingPaneState';
export type {
    default as SxSExtendedViewState,
    TextPreviewCustomEncoding,
} from './store/schema/SxSExtendedViewState';
export { default as AttachmentPreviewMode } from './store/schema/AttachmentPreviewMode';
